import { Agent } from "@biblioteksentralen/cordata";
import { Box, Heading, VStack } from "@biblioteksentralen/react";
import { sort } from "radash";
import { DefaultContainer } from "../../../components/ContentContainer";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { formatAgentName } from "../../cordata/agents";
import { ContributorRoleCode, getContributorRoleLabel, getContributorRoleSortIndex } from "../../cordata/roles";
import { ExternalAgentData } from "../../externalDataFetchers/fetchExternalAgentData";
import { FacetLinks } from "../SubjectLinks";
import { AgentViewBanner } from "./AgentViewBanner";
import { AgentWorksSection } from "./AgentWorksSection";
import { CordataSearchFacetTerm } from "@libry-content/integrations";
import { AgentWorksData } from "../../../app/[locale]/sites/[siteDomain]/samling/personer/[agentBibbiIdAndNorafId]/fetchAgentWorksData";

const sortWorksByRole = <T,>(worksByContributorRoleLabel: Partial<Record<ContributorRoleCode, T>>) =>
  sort(Object.entries(worksByContributorRoleLabel) as [ContributorRoleCode, T][], ([roleCode]) =>
    getContributorRoleSortIndex(roleCode)
  );

type AgentViewProps = {
  agent: Agent;
  externalData: ExternalAgentData | null;
  initialAgentWorksData: AgentWorksData;
  subjectsFromfacet: CordataSearchFacetTerm[] | null;
};

export const AgentView = ({ agent, externalData, initialAgentWorksData, subjectsFromfacet }: AgentViewProps) => {
  const { t, ts, lang } = useTranslation();

  const name = formatAgentName(ts, agent);

  const worksDataForContributorRoles = initialAgentWorksData.worksDataForContributorRoles;

  // Works for which the agent is both contributor and subject (e.g. autobiographies) are only shown
  // in the contributor section. Among other reasons this is to avoid the two showing representative
  // manifestations with different languages etc.

  const sortedWorksDataForContributorRoles =
    worksDataForContributorRoles && sortWorksByRole(worksDataForContributorRoles);

  return (
    <DefaultContainer position="relative" margin="0 auto" padding="0 1rem">
      <AgentViewBanner agent={agent} externalData={externalData}></AgentViewBanner>
      <VStack alignItems="flex-start" spacing="4rem" marginTop="3rem">
        {sortedWorksDataForContributorRoles?.map(([roleCode, worksDataForSection]) => (
          <AgentWorksSection
            key={roleCode}
            agentWorksSectionUrlParam={roleCode}
            heading={`${t("Som")} ${getContributorRoleLabel(lang, roleCode)}`}
            // TODO: make it possible to pick representative WorkTriple based on language signature. Removing it for simplicity for now
            // getRepresentativeLanguages={(work) => contributorRepresentativeLanguages(work, agent.id, roleCode)}
            agent={agent}
            initialAgentWorksData={worksDataForSection}
          />
        ))}
        {!!subjectsFromfacet?.length && (
          <Box as="section">
            <Heading as="h2" fontSize="3xl" marginBottom="1rem">
              {t("Emner knyttet til {personNavn}", { personNavn: name })}
            </Heading>
            <FacetLinks facetTerms={subjectsFromfacet} />
          </Box>
        )}
        {!!initialAgentWorksData.worksDataAboutAgent?.worksParams.length && (
          <AgentWorksSection
            agentWorksSectionUrlParam={"aboutAgent"}
            heading={t("Om {personNavn}", { personNavn: name })}
            agent={agent}
            initialAgentWorksData={initialAgentWorksData.worksDataAboutAgent}
          />
        )}
        {/* TODO: Inkludere anbefalinger på lignende forfattere? */}
      </VStack>
    </DefaultContainer>
  );
};
